import * as React from "react";
import { DeviceTypes } from "../../misc/api/apiTypes";
import { RecordingStartItem } from "./CorrectionTypes/RecordingStart/RecordingStartItem";
import { SubjectIdItem } from "./CorrectionTypes/SubjectId/SubjectIdItem";
import { Correction, MiscCorrection, VisibleWizards } from "../Request/DcfRequestReducer";
import { DcfRequestAction, SetVisibleWizardAction } from "../Request/DcfRequestActions";
import { MiscCorrectionItem } from "./CorrectionTypes/Misc/MiscCorrectionItem";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { EntityLabelsItem } from "./CorrectionTypes/EntityLabels/EntityLabelsItem";
import { EntityLabelGroup } from "./CorrectionTypes/EntityLabels/EntityLabelCorrectionWizard";
import { SubjectTypeItem } from "./CorrectionTypes/SubjectType/SubjectTypeItem";
import { SuppressCoughSessionItem } from "./CorrectionTypes/SuppressCoughSession/SuppressCoughSessionItem";
import { MediaSerialItem } from './CorrectionTypes/MediaSerial/MediaSerialItem';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Subject, Visit, Session, CoughSession, WebPortalClient } from "webportalclient";
import { SwapLabelsItem } from "./CorrectionTypes/SwapLabels/SwapLabelsItem";
import { ConfigurableDataItemIds } from "../../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export interface CorrectionTypeSelectionProps {
    subject: Subject;

    miscCorrection: MiscCorrection;
    corrections: Correction[];
    recordId: string;
    allVisitsSelected: boolean;
    allSessionsSelected: boolean;
    session: Session;
    visit: Visit;

    dispatch: React.Dispatch<DcfRequestAction>;
}

const isCoughSession = (model: Session): model is CoughSession => {
    if (!model) return false;

    return model.sessionType === SessionTypes.Cough;
}

const GetSpecificSession = async (client: WebPortalClient, session: Session): Promise<Session> => {
    switch (session.sessionType) {
        case SessionTypes.Cough:
            var coughSession = await client.getCoughSession(session.id, `suppressed,mediaSerialNumber,name,id,occurred,recordingStart,sessionType,deviceType`);
            session.sessionScheduleItemId;
            coughSession['sessionScheduleItemId'] = session.sessionScheduleItemId;
            return coughSession;
        default:
            return session;
    }
};

export const CorrectionTypeSelection = ({
    recordId,
    subject,
    visit,
    session,
    corrections,
    miscCorrection,
    allVisitsSelected,
    allSessionsSelected,
    dispatch
}: CorrectionTypeSelectionProps): JSX.Element => {
    const [specificSession, setSpecificSession] = React.useState(session);
    const [isFetchingSpecificSession, setIsFetchingSpecificSession] = React.useState(false);
    const client = useWebPortalApiClient();
    const { dataItemConfigurationCollection } = useCurrentStudyConfigurationContext();

    React.useEffect(
        () => {
            const fetchSpecificSession = async () => {
                setIsFetchingSpecificSession(true);
                const sessionEntity = await GetSpecificSession(client.client, session);

                setSpecificSession(sessionEntity);
                setIsFetchingSpecificSession(false);
            };

            if (session && session.id) {
                fetchSpecificSession();
            }

            return (): void => setSpecificSession(null);
        },
        [session]
    );

    const isItemDisabled = (correctionName: VisibleWizards): boolean => {
        return (
            corrections.filter(x => x.sourceCorrection !== correctionName).length > 0 ||
            !!miscCorrection
        );
    };

    const isItemSelected = (correctionName: VisibleWizards): boolean => {
        return corrections.filter(x => x.sourceCorrection === correctionName).length > 0;
    };

    const subjectConfig = () => dataItemConfigurationCollection.find(dic => dic.configurableDataItemId == ConfigurableDataItemIds.SubjectId);

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h3 className="text-primary">Requested Data Corrections</h3>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    {isFetchingSpecificSession ? (
                        <LoadingWithMessage message="Loading Possible Data Corrections..." />
                    ) : (
                        <ul className="posh-select">
                                {isCoughSession(specificSession) && (specificSession as CoughSession).recordingStart ? (
                                <RecordingStartItem
                                    recordId={recordId}
                                    isDisabled={isItemDisabled(VisibleWizards.RecordingStart)}
                                    correctionSelected={isItemSelected(VisibleWizards.RecordingStart)}
                                    onClick={() =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.RecordingStart,
                                                (specificSession as CoughSession).recordingStart
                                            )
                                        )
                                    }
                                />
                            ) : null}
                                {!!subject &&
                                    !subject.createdWithSpirotracV &&
                                    !visit &&
                                    !specificSession &&
                                    !allVisitsSelected &&
                                    !allSessionsSelected ? (
                                <SubjectIdItem
                                    subjectDisplayText={subjectConfig().displayText}
                                    recordId={recordId}
                                        isDisabled={isItemDisabled(VisibleWizards.SubjectNumber)}
                                        correctionSelected={isItemSelected(VisibleWizards.SubjectNumber)}
                                    onClick={() =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.SubjectNumber,
                                                subject.number
                                            )
                                        )
                                    }
                                />
                            ) : null}
                            {!!subject &&
                            !subject.createdWithSpirotracV &&
                            !visit &&
                            !specificSession &&
                            !allVisitsSelected &&
                            !allSessionsSelected ? (
                                <SubjectTypeItem
                                    recordId={recordId}
                                            isDisabled={isItemDisabled(VisibleWizards.SubjectType)}
                                            correctionSelected={isItemSelected(VisibleWizards.SubjectType)}
                                    onClick={() =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.SubjectType,
                                                subject.status
                                            )
                                        )
                                            } />
                                ) : null}
                                {isCoughSession(specificSession) ? <SuppressCoughSessionItem
                                    recordId={recordId}
                                    isDisabled={isItemDisabled(VisibleWizards.CoughSessionSuppress)}
                                    correctionSelected={isItemSelected(VisibleWizards.CoughSessionSuppress)}
                                    onClick={() => {
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.CoughSessionSuppress,
                                                specificSession.suppressed.toString()
                                            )
                                        )
                                    }} />
                                : null}
                                {!!subject && !subject.createdWithSpirotracV && specificSession ? (
                                    <EntityLabelsItem
                                        recordId={recordId}
                                        isDisabled={isItemDisabled(VisibleWizards.EntityLabels)}
                                        correctionSelected={isItemSelected(VisibleWizards.EntityLabels)}
                                        onClick={() =>
                                            dispatch(
                                                SetVisibleWizardAction.create(
                                                    recordId,
                                                    VisibleWizards.EntityLabels,
                                                    {
                                                        subject: {
                                                            label: subject.number,
                                                            id: subject.id
                                                        },
                                                        visit: visit
                                                            ? {
                                                                label: visit.name,
                                                                id: visit.visitScheduleItemId
                                                            }
                                                            : undefined,
                                                        session: specificSession
                                                            ? {
                                                                label: specificSession.name,
                                                                id: specificSession.sessionScheduleItemId
                                                            }
                                                            : undefined
                                                    } as EntityLabelGroup
                                                )
                                            )
                                        }
                                    />
                                ) : null}
                                {!!subject && !subject.createdWithSpirotracV && specificSession ? (
                                    <SwapLabelsItem
                                        recordId={recordId}
                                        isDisabled={isItemDisabled(VisibleWizards.SwapLabels)}
                                        correctionSelected={isItemSelected(VisibleWizards.SwapLabels)}
                                        onClick={() =>
                                            dispatch(
                                                SetVisibleWizardAction.create(
                                                    recordId,
                                                    VisibleWizards.SwapLabels,
                                                    {
                                                        subject: {
                                                            number: subject.number,
                                                            id: subject.id
                                                        },
                                                        visit: visit
                                                            ? {
                                                                name: visit.name,
                                                                id: visit.id
                                                            }
                                                            : undefined,
                                                        session: specificSession
                                                            ? {
                                                                name: specificSession.name,
                                                                id: specificSession.id,
                                                                occurred: specificSession.occurred,
                                                                suppressed: specificSession.suppressed
                                                            }
                                                            : undefined
                                                    }
                                                )
                                            )
                                        }
                                    />
                                ) : null}
                                {!!subject && isCoughSession(specificSession) && specificSession.deviceType === DeviceTypes.VitaloJAK1 ? <MediaSerialItem
                                    recordId={recordId}
                                    isDisabled={isItemDisabled(VisibleWizards.MediaSerial)}
                                    correctionSelected={isItemSelected(VisibleWizards.MediaSerial)}
                                    onClick={(): void =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.MediaSerial,
                                                (specificSession as CoughSession).mediaSerialNumber
                                            )
                                        )
                                    }
                                /> : null}
                                <MiscCorrectionItem
                                    recordId={recordId}
                                    isDisabled={corrections.length >= 1}

                                    correctionSelected={!!miscCorrection}
                                    onClick={(): void =>
                                        dispatch(SetVisibleWizardAction.create(recordId, VisibleWizards.Misc, null))
                                    }
                                />
                            </ul>
                    )}
                </div>
            </div>
        </>
    );
};
